import { FC, HTMLAttributes, memo, ReactNode } from 'react';

import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';
import usePlankTableStore from '@/views/ddp/hooks/usePlankTableStore';
import useTableKnoop from '@/views/ddp/hooks/useTableKnoop';
import { AtomicKnoopMaterieel } from '@/views/ddp/state/types/VoorraadbeheerStore';
import { Reserve } from '@/views/ddp/types';
import { adjustMatTypeLength } from '@/views/ddp/utils/table';

import {
  conflictToSelectionItem,
  defectToSelectionItem,
  reserveToSelectionItem,
} from '../../../utils/plankSelectionItems';
import ConflictListItem from '../../conflict/ConflictListItem';
import DefectListItem from '../../defect/DefectListItem';
import ReserveListItem from '../../reserve/ReserveListItem';
import VoorraadRegionaalListItem from '../../VoorraadBeheer/VoorraadRegionaalListItem';
import TableList, { TableListType } from '../TableList/TableList';

import plankStyles from '../../../PlankView.module.css';
import styles from '../Table.module.css';

type TableRowRegionaalType = HTMLAttributes<HTMLDivElement> & {
  knoop: string;
};
const ConflictList = memo((props: TableListType<Conflict>) => (
  <TableList {...props} toSelectionItem={conflictToSelectionItem} />
));
const ConflictRow: (conflict: Conflict) => ReactNode = (conflict: Conflict) => <ConflictListItem conflict={conflict} />;

const DefectList = memo((props: TableListType<Defect>) => (
  <TableList {...props} toSelectionItem={defectToSelectionItem} />
));
const DefectRow: (defect: Defect) => ReactNode = (defect: Defect) => <DefectListItem defect={defect} />;
const OnderhoudsRow: (defect: Defect) => ReactNode = (defect: Defect) => (
  <DefectListItem isOnderhoudHerstel defect={defect} />
);

const ReserveList = memo((props: TableListType<Reserve>) => (
  <TableList {...props} toSelectionItem={reserveToSelectionItem} />
));
const ReserveRow: (reserve: Reserve) => ReactNode = (reserve: Reserve) => <ReserveListItem reserve={reserve} />;

const VoorraadList = memo<TableListType<AtomicKnoopMaterieel>>(TableList);
const VoorraadRegionaalRow: (voorraadRegionaal: AtomicKnoopMaterieel) => ReactNode = (
  voorraadRegionaal: AtomicKnoopMaterieel,
) => <VoorraadRegionaalListItem voorraadRegionaal={voorraadRegionaal} />;
const TableRowRegionaal: FC<TableRowRegionaalType> = ({ knoop }) => {
  const isRowCollapsed = usePlankTableStore((state) => state.isRegionaalRowCollapsed(knoop));
  const showColumn = usePlankTableStore((state) => state.showColumn);
  const toggleCollapseRegionaalRow = usePlankTableStore((state) => state.toggleCollapseRegionaalRow);

  const {
    mancos,
    verkeerdMat,
    defectenInzetbaar,
    defectenBeperktInzetbaar,
    defectenNietInzetbaar,
    defectenInOnderhoud,
    reserves,
    voorraadKnoop,
  } = useTableKnoop(knoop);
  return (
    <div className={styles.ddpDataRowStyle}>
      <div
        className={`row-first${adjustMatTypeLength(knoop) === knoop ? '' : '-wrap'}`}
        style={{
          position: 'sticky',
          backgroundColor: 'var(--tractie-color-background-default)',
          padding: '0 24px',
          left: 0,
          zIndex: 21,
        }}
      >
        <h2 className={plankStyles.plankH2}>{adjustMatTypeLength(knoop)}</h2>
      </div>
      <div className={styles.ddpDataRowInnerStyle}>
        <div className={styles.tableRowBaseStyle}>
          {showColumn('Voorraadbeheer') && (
            <VoorraadList
              data={voorraadKnoop.matList}
              renderRow={VoorraadRegionaalRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
              itemHeight={32}
              tableHeight={257}
            />
          )}
          {showColumn('Manco') && (
            <ConflictList
              data={mancos}
              renderRow={ConflictRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
            />
          )}
          {showColumn('Reserve') && (
            <ReserveList
              dynamic
              data={reserves}
              estimateSize={
                // Size is = (32 sm/58 xl) + 2
                (index: number) => (reserves[index].buitenDienstTijd ? 60 : 34)
              }
              renderRow={ReserveRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
            />
          )}
          {showColumn('Verkeerd mat') && (
            <ConflictList
              data={verkeerdMat}
              renderRow={ConflictRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
            />
          )}
          {showColumn('Defect inzetbaar') && (
            <DefectList
              isDefect
              data-testid={`${knoop}_defect_inzetbaar_data_list`}
              data={defectenInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
              totalTestId={`${knoop}_defect_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Defect beperkt inzetbaar') && (
            <DefectList
              isDefect
              data-testid={`${knoop}_defect_beperkt_inzetbaar_data_list`}
              data={defectenBeperktInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
              totalTestId={`${knoop}_defect_beperkt_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Defect niet inzetbaar') && (
            <DefectList
              isDefect
              data={defectenNietInzetbaar}
              renderRow={DefectRow}
              collapse={isRowCollapsed}
              onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
              totalTestId={`${knoop}_defect_niet_inzetbaar_data_list_total${isRowCollapsed ? '_collapsed' : ''}`}
            />
          )}
          {showColumn('Onderhoud & herstel') && (
            <div>
              <DefectList
                isDefect
                data={defectenInOnderhoud}
                renderRow={OnderhoudsRow}
                collapse={isRowCollapsed}
                onTotalClick={() => toggleCollapseRegionaalRow(knoop)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default TableRowRegionaal;
